import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { PaginatedFetch, PaginatedMeta, Transaction } from '@types'
import { PAGE_SIZE } from '@/const/common'

import { prepareResponseError } from '../utils/helpers'

import api, { getPaginatedRequest } from '@/store/api'
import useAnalyticsStore from '@/store/analytics'
import { useRepositoriesStore } from '@/store/repositories/index'

export const useTransactionsDeletedStore = defineStore(
  'transactions-deleted',
  () => {
    // INIT
    const repositoriesStore = useRepositoriesStore()
    const analyticsStore = useAnalyticsStore()

    const list = ref<Transaction[]>([])
    const pagination = ref<PaginatedMeta>()

    const initFlag = ref(false)

    const loading = ref(true)

    let abortController = new AbortController()

    const repositoryId = computed(() => repositoriesStore.getCurrentId)

    // GETTERS

    const getList = computed(() => list.value)
    const getPagination = computed(() => pagination.value)

    // SETTERS

    const setList = (data: Transaction[], meta?: PaginatedMeta) => {
      list.value = data
      pagination.value = meta
    }

    const fetch: PaginatedFetch = async (
      options,
      store = true,
      signal?: AbortSignal,
    ): Promise<void> => {
      if (!repositoryId.value) return
      if (store) loading.value = true
      try {
        const { data, meta } = await getPaginatedRequest<Transaction>(
          'transactions',
          {
            repository_id: repositoryId.value,
            show_deleted: true,
            page_size: PAGE_SIZE,
            ...options,
          },
          signal,
        )
        setList(data, meta)
        initFlag.value = true
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        if (store) loading.value = false
      }
    }

    const restore = async (id: string, analyticsDeprecated = true): Promise<void> => {
      try {
        await api.post(`transactions/${id}/restore`, {})
        analyticsDeprecated && analyticsStore.markAsDeprecated()
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const bulkRestore = async (ids: string[]): Promise<void> => {
      try {
        await api.post(
          `transactions/restore?repository_id=${repositoryId.value}`,
          ids,
        )
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const permanentDelete = async (ids: string[]): Promise<void> => {
      try {
        await api.delete('transactions?is_hard=1', { data: ids })
        await fetch()
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const emptyTrash = async (): Promise<void> => {
      try {
        await api.delete('transactions/trash/empty', {
          data: {
            repository_id: repositoryId.value,
          },
        })
        await fetch()
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const cancel = () => {
      abortController.abort()
      abortController = new AbortController()
    }

    const clear = () => {
      initFlag.value = false
      setList([], undefined)
    }

    return {
      initFlag,
      loading,

      getList,
      getPagination,

      fetch,

      restore,
      bulkRestore,
      permanentDelete,
      emptyTrash,

      cancel,
      clear,
    }
  },
)
